import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  FiBell, FiBookmark, FiCheck, FiCircle, FiHeart, FiMessageCircle, FiSend, FiShare2, FiTrash2, FiUserPlus,
} from 'react-icons/fi';
import { cDeleteNotification, cNotifications, cReadNotifications } from '../../api';
import { useCommunity } from '../../contexts/CommunityContext';
import { useLanguage } from '../../i18n';
import UserAvatar from '../../components/community/UserAvatar';
import { NAVY, BLUE, timeAgo } from '../../components/community/brand';

const ICONS = {
  like: <FiHeart className="w-3.5 h-3.5" />,
  comment: <FiMessageCircle className="w-3.5 h-3.5" />,
  follow: <FiUserPlus className="w-3.5 h-3.5" />,
  share: <FiShare2 className="w-3.5 h-3.5" />,
  save: <FiBookmark className="w-3.5 h-3.5" />,
  message: <FiSend className="w-3.5 h-3.5" />,
  story: <FiCircle className="w-3.5 h-3.5" />,
  admin: <FiBell className="w-3.5 h-3.5" />,
  broadcast: <FiBell className="w-3.5 h-3.5" />,
  post: <FiBell className="w-3.5 h-3.5" />,
};

/** Notification centre: likes, comments, follows, shares, messages, admin news. */
export default function NotificationsPage() {
  const { t, lang } = useLanguage();
  const { user, requireAuth, refresh, alerts, enableAlerts } = useCommunity();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    cNotifications({ limit: 40 })
      .then((res) => { setItems(res?.items || []); setUnread(res?.unread || 0); })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!requireAuth(t('loginRequiredDesc'))) { setLoading(false); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const open = (n) => {
    if (n.post_id) navigate(`/post/${n.post_id}`);
    else if (n.type === 'message') navigate('/messages');
    else if (n.type === 'story') navigate('/');
    else if (n.actor) navigate(`/u/${n.actor.username}`);
  };

  const markAll = async () => {
    try {
      await cReadNotifications();
      setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnread(0);
      refresh();
      toast.success(t('saved'));
    } catch (e) {
      toast.error(t('somethingWrong'));
    }
  };

  const remove = async (n) => {
    try {
      await cDeleteNotification(n.id);
      setItems((prev) => prev.filter((x) => x.id !== n.id));
    } catch (e) { /* ignore */ }
  };

  if (!user) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center">
        <p className="font-bold">{t('loginRequiredTitle')}</p>
        <p className="text-sm text-gray-500 mt-1">{t('loginRequiredDesc')}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-3 lg:px-4 py-4 space-y-3">
      <header className="flex items-center justify-between">
        <h1 className="font-extrabold text-lg inline-flex items-center gap-2">
          <FiBell className="w-5 h-5" style={{ color: BLUE }} /> {t('notificationNav')}
          {unread > 0 && (
            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full text-white" style={{ background: BLUE }}>
              {unread}
            </span>
          )}
        </h1>
        {items.length > 0 && (
          <button
            type="button"
            onClick={markAll}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold text-white"
            style={{ background: NAVY }}
          >
            <FiCheck className="w-3.5 h-3.5" /> {t('markAllRead')}
          </button>
        )}
      </header>

      {/* Ask the browser for permission once — then alerts arrive in the background */}
      {alerts !== 'granted' && (
        <button
          type="button"
          onClick={() => enableAlerts()}
          className="w-full text-left bg-white dark:bg-gray-900 rounded-2xl border border-dashed border-gray-300 dark:border-gray-700 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          <span className="inline-flex items-center gap-2 text-sm font-bold">
            🔔 {t('enableAlerts')}
          </span>
          <span className="block text-[11px] text-gray-500 mt-0.5">{t('alertsHint')}</span>
        </button>
      )}

      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 divide-y divide-gray-100 dark:divide-gray-800">
        {loading ? (
          <p className="p-4 text-sm text-gray-400">{t('loading')}</p>
        ) : items.length === 0 ? (
          <p className="p-6 text-sm text-gray-400 text-center">{t('noNotifications')}</p>
        ) : (
          items.map((n) => (
            <div key={n.id} className={`flex items-start gap-3 p-3.5 ${n.is_read ? '' : 'bg-blue-50/50 dark:bg-gray-800/40'}`}>
              <UserAvatar user={n.actor || { name: 'Mini Shop' }} size={40} to={n.actor ? `/u/${n.actor.username}` : null} />
              <button type="button" onClick={() => open(n)} className="flex-1 min-w-0 text-left">
                <p className="text-sm font-medium flex items-center gap-1.5">
                  <span style={{ color: BLUE }}>{ICONS[n.type] || ICONS.broadcast}</span>
                  <span className="line-clamp-2">{n.text}</span>
                </p>
                <span className="text-[11px] text-gray-400">{timeAgo(n.created_at, lang)}</span>
              </button>
              <button
                type="button"
                onClick={() => remove(n)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-gray-800"
                aria-label={t('delete')}
              >
                <FiTrash2 className="w-4 h-4" />
              </button>
            </div>
          ))
        )}
      </div>

      <p className="text-center text-xs text-gray-400">
        <Link to="/" className="underline">{t('backToFeed')}</Link>
      </p>
    </div>
  );
}
